import type { CorruptionTier, CorruptionTierBuff, Unit } from "@sins2-atlas/game-data";
import { CORRUPTION_TIERS } from "@sins2-atlas/game-data";
import { clsx } from "clsx";
import { dataset } from "../lib/dataset";
import { exactNumber } from "../lib/format";
import type { StatFrame } from "../lib/frame";
import { corruptionLabel } from "../lib/frame-labels";
import { Card } from "./card";

// The Corruption card (Issue 20, ADR 0007; design mock 2026-09-04): the whole tier story of
// one unit in the game's own words. It sat in the Survivability cell until 2026-09-07, when
// the user moved it LAST on the sheet, after every stat group. Everything here is composed by
// extraction — the pool's three figures under the game's labels, each tier's buffs with their
// names, tooltip lines, mutation labels and description. This renders; it never computes, and
// it never says "tier" (CONTEXT.md: Corruption Tier).
//
// The per-race blurb the dataset still carries is deliberately NOT rendered (2026-09-07): it
// explains the mechanic rather than stating a figure, and the site presents data.

function PoolFigure({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="flex flex-col gap-0.5 px-4 py-2.5">
      <dt className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted">{label}</dt>
      <dd className="font-mono text-[15px] text-ink [font-variant-numeric:tabular-nums]">
        {value}
        {unit && <span className="ml-0.5 text-[11px] text-ink-3">{unit}</span>}
      </dd>
    </div>
  );
}

/** A tooltip line's figure, coloured by its buff's scope the way the comparison colours a
 * best value: a gain reads as best, a loss as the unverified amber — the two accents the app
 * already owns (ADR 0005: we do not mirror the game's own colours). The SCOPE decides, never
 * the figure's sign (review round 2026-09-05): a negative-scope line is a penalty whichever
 * way it counts — Advent's "Ability Cooldown +15%", Vasari's "Phase Jump Charge Time +36%". */
export function buffLineTone(scope: CorruptionTierBuff["scope"]): string {
  return scope === "negative" ? "text-unverified" : "text-best";
}

function BuffBlock({ buff }: { buff: CorruptionTierBuff }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={clsx(
          "text-[12.5px] font-medium",
          buff.scope === "negative" ? "text-ink-3" : "text-ink-2",
        )}
      >
        {buff.name}
      </span>
      {buff.lines.map((line) => (
        <div
          key={`${line.label}|${line.value}`}
          className="flex items-baseline justify-between gap-3 pl-2.5 text-[13px] text-ink-3"
        >
          <span>{line.label}</span>
          <span
            className={clsx(
              "font-mono text-[13px] [font-variant-numeric:tabular-nums]",
              buffLineTone(buff.scope),
            )}
          >
            {line.value}
          </span>
        </div>
      ))}
      {buff.mutations.map((mutation) => (
        <div key={mutation} className="pl-2.5 text-[13px] text-unverified">
          {mutation}
        </div>
      ))}
      {/* A buff with no numeric line tells its story in the game's sentence (the Eidolon
          hull drain: the rate is a time action the game never labels, so it never becomes
          a number here — ADR 0007). */}
      {buff.lines.length === 0 && (
        <p className="pl-2.5 text-[13px] italic text-ink-3 whitespace-pre-line">
          {buff.description}
        </p>
      )}
    </div>
  );
}

function TierBlock({
  tier,
  corruption,
  active,
}: {
  tier: CorruptionTier;
  corruption: NonNullable<Unit["corruption"]>;
  active: boolean;
}) {
  const block = corruption.tiers[tier];
  const threshold = corruption.thresholds[tier];
  return (
    <div
      className={clsx(
        "flex flex-col gap-1.5 border-t border-line-faint px-4 py-2.5",
        active && "bg-raised",
      )}
      aria-current={active ? "true" : undefined}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className={clsx("text-[13px] font-semibold", active ? "text-ink" : "text-ink-2")}>
          {corruptionLabel(tier)}
        </span>
        <span className="font-mono text-[11px] text-muted [font-variant-numeric:tabular-nums]">
          at {Math.round(threshold * 100)}% ·{" "}
          {exactNumber(Math.round(threshold * corruption.maxCorruption))}
        </span>
      </div>
      {block.buffs.map((buff) => (
        <BuffBlock key={buff.id} buff={buff} />
      ))}
    </div>
  );
}

/** The card renders exactly for units with a pool; strikecraft and Envoys have none and
 * get no card (the Corruption row already says so on their sheet). */
export function CorruptionCard({ unit, frame }: { unit: Unit; frame: StatFrame }) {
  const corruption = unit.corruption;
  if (corruption === undefined) return null;
  const labels = dataset.corruptionLabels;
  return (
    <Card title="Corruption" inset="snug">
      <dl className="grid grid-cols-2 border-b border-line-faint sm:grid-cols-3">
        <PoolFigure label={labels.maxCorruption} value={exactNumber(corruption.maxCorruption)} />
        <PoolFigure label={labels.decayRate} value={exactNumber(corruption.decayRate)} unit="/s" />
        <PoolFigure label={labels.decayDelay} value={exactNumber(corruption.decayDelay)} unit="s" />
      </dl>
      {CORRUPTION_TIERS.map((tier) => (
        <TierBlock
          key={tier}
          tier={tier}
          corruption={corruption}
          active={frame.corruption === tier}
        />
      ))}
    </Card>
  );
}
